/**
 * AppRegistryContext — shares the server-side app registry with the shell.
 *
 * The registry controls which apps are visible to non-admin/non-developer
 * users. It is fetched once on login and cached for the session.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAccount } from './AccountContext';
import { getSessionToken } from '@yada/storage';
import { AUTH_API_URL } from '../config/api';

export interface AppRegistryEntry {
  appId: string;
  enabled: boolean;
  releaseState: 'wip' | 'pre_released' | 'released';
}

interface AppRegistryContextValue {
  registry: AppRegistryEntry[];
  /** True if a fetch is in progress on initial load */
  loading: boolean;
  /** Reload the registry from the server (e.g. after admin toggles an app) */
  refresh: () => Promise<void>;
}

const AppRegistryContext = createContext<AppRegistryContextValue>({
  registry: [],
  loading: false,
  refresh: async () => {},
});

export const AppRegistryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { account } = useAccount();
  const [registry, setRegistry] = useState<AppRegistryEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchRegistry = async () => {
    try {
      const token = await getSessionToken();
      if (!token) return;
      const res = await fetch(`${AUTH_API_URL}/api/app-registry`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRegistry(data.apps ?? []);
      }
    } catch {
      // Non-fatal: fall back to showing all apps
    }
  };

  const accountId = account?.id;
  useEffect(() => {
    if (!accountId) return;
    setLoading(true);
    fetchRegistry().finally(() => setLoading(false));
  }, [accountId]);

  return (
    <AppRegistryContext.Provider value={{ registry, loading, refresh: fetchRegistry }}>
      {children}
    </AppRegistryContext.Provider>
  );
};

export function useAppRegistry(): AppRegistryContextValue {
  return useContext(AppRegistryContext);
}
